import { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Plus, FileSignature, Edit2, Trash2, Upload, ExternalLink, Download, FileText, Lock } from 'lucide-react';
import { useAuth, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CONTRACT_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'lease', label: 'Lease Agreement' },
  { value: 'sale', label: 'Sale Contract' },
  { value: 'boarding', label: 'Boarding Agreement' },
  { value: 'training', label: 'Training Agreement' },
  { value: 'lesson', label: 'Lesson Agreement' },
  { value: 'trial', label: 'Trial Agreement' }
];

// Form component defined OUTSIDE main component to prevent re-render issues
const ContractForm = ({ data, onChange, onSubmit, isEdit = false, horses, riders, selectedFile, onFileSelect, fileInputRef }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Title *</Label>
      <Input
        value={data.title || ''}
        onChange={(e) => onChange({ ...data, title: e.target.value })}
        placeholder="e.g., Boarding Agreement - Midnight Star"
        required
        data-testid="contract-title-input"
      />
    </div>

    <div className="space-y-2">
      <Label>Contract Type</Label>
      <Select value={data.contract_type || 'general'} onValueChange={(v) => onChange({ ...data, contract_type: v })}>
        <SelectTrigger data-testid="contract-type-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONTRACT_TYPES.map(t => (
            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Horse (optional)</Label>
        <Select value={data.horse_id || 'none'} onValueChange={(v) => onChange({ ...data, horse_id: v === 'none' ? '' : v })}>
          <SelectTrigger data-testid="contract-horse-select">
            <SelectValue placeholder="Select horse" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {horses.map(h => (
              <SelectItem key={h.horse_id} value={h.horse_id}>{h.stable_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Rider (optional)</Label>
        <Select value={data.rider_id || 'none'} onValueChange={(v) => onChange({ ...data, rider_id: v === 'none' ? '' : v })}>
          <SelectTrigger data-testid="contract-rider-select">
            <SelectValue placeholder="Select rider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {riders.map(r => (
              <SelectItem key={r.rider_id} value={r.rider_id}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Start Date</Label>
        <Input
          type="date"
          value={data.start_date || ''}
          onChange={(e) => onChange({ ...data, start_date: e.target.value })}
          data-testid="contract-start-date-input"
        />
      </div>

      <div className="space-y-2">
        <Label>End Date</Label>
        <Input
          type="date"
          value={data.end_date || ''}
          onChange={(e) => onChange({ ...data, end_date: e.target.value })}
          data-testid="contract-end-date-input"
        />
      </div>
    </div>

    {!isEdit && (
      <div className="space-y-2">
        <Label>Contract File (optional)</Label>
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-forest transition-colors ${selectedFile ? 'border-forest bg-forest/5' : 'border-muted'}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {selectedFile ? (
              <span className="text-forest font-medium">{selectedFile.name}</span>
            ) : (
              'Click to upload PDF or document'
            )}
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          onChange={(e) => onFileSelect(e.target.files?.[0] || null)}
          className="hidden"
          accept=".pdf,.doc,.docx"
        />
      </div>
    )}

    <div className="space-y-2">
      <Label>Notes</Label>
      <Textarea
        value={data.notes || ''}
        onChange={(e) => onChange({ ...data, notes: e.target.value })}
        placeholder="Additional notes..."
        data-testid="contract-notes-input"
      />
    </div>

    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-contract-btn">
      {isEdit ? 'Update Contract' : 'Create Contract'}
    </Button>
  </form>
);

export const Contracts = () => {
  const { token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const [contracts, setContracts] = useState([]);
  const [horses, setHorses] = useState([]);
  const [riders, setRiders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const emptyContract = {
    title: '',
    horse_id: '',
    rider_id: '',
    contract_type: 'general',
    start_date: '',
    end_date: '',
    notes: ''
  };

  const [newContract, setNewContract] = useState(emptyContract);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [contractsRes, horsesRes, ridersRes, templatesRes] = await Promise.all([
        fetch(`${API}/contracts`, { headers }),
        fetch(`${API}/horses`, { headers }),
        fetch(`${API}/rider-profiles`, { headers }),
        fetch(`${API}/templates`, { headers })
      ]);

      if (contractsRes.ok) setContracts(await contractsRes.json());
      if (horsesRes.ok) setHorses(await horsesRes.json());
      if (ridersRes.ok) setRiders(await ridersRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContract = async (e) => {
    e.preventDefault();
    if (!newContract.title.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      const response = await fetch(`${API}/contracts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          ...newContract,
          horse_id: newContract.horse_id || null,
          rider_id: newContract.rider_id || null
        })
      });

      if (response.ok) {
        const contract = await response.json();

        // Upload file if selected
        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
          const uploadHeaders = {};
          if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
          if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
          
          const uploadRes = await fetch(`${API}/upload/contract/${contract.contract_id}`, {
            method: 'POST',
            headers: uploadHeaders,
            body: formData
          });
          if (uploadRes.ok) {
            const data = await uploadRes.json();
            contract.file_url = data.file_url;
          }
        }

        setContracts([...contracts, contract]);
        setDialogOpen(false);
        setNewContract(emptyContract);
        setSelectedFile(null);
        toast.success('Contract created!');
      }
    } catch (error) {
      toast.error('Failed to create contract');
    }
  };

  const handleEditContract = async (e) => {
    e.preventDefault();
    if (!editingContract) return;

    try {
      const response = await fetch(`${API}/contracts/${editingContract.contract_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          title: editingContract.title,
          horse_id: editingContract.horse_id || null,
          rider_id: editingContract.rider_id || null,
          contract_type: editingContract.contract_type,
          start_date: editingContract.start_date,
          end_date: editingContract.end_date,
          notes: editingContract.notes
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setContracts(contracts.map(c => c.contract_id === updated.contract_id ? updated : c));
        setEditDialogOpen(false);
        setEditingContract(null);
        toast.success('Contract updated!');
      }
    } catch (error) {
      toast.error('Failed to update contract');
    }
  };

  const handleDeleteContract = async (contractId) => {
    try {
      const response = await fetch(`${API}/contracts/${contractId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setContracts(contracts.filter(c => c.contract_id !== contractId));
        toast.success('Contract deleted');
      }
    } catch (error) {
      toast.error('Failed to delete contract');
    }
  };

  const handleDownloadTemplate = (templateId, filename) => {
    window.open(`${API}/templates/${templateId}`, '_blank');
  };

  const getHorseName = (horseId) => {
    if (!horseId) return null;
    const horse = horses.find(h => h.horse_id === horseId);
    return horse?.stable_name;
  };

  const getRiderName = (riderId) => {
    if (!riderId) return null;
    const rider = riders.find(r => r.rider_id === riderId);
    return rider?.name;
  };

  const getContractTypeLabel = (type) => {
    const found = CONTRACT_TYPES.find(t => t.value === type);
    return found?.label || type;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin w-8 h-8 border-4 border-forest border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Contracts Vault</h1>
            <p className="text-muted-foreground mt-1">
              {contracts.length} {contracts.length === 1 ? 'contract' : 'contracts'}
            </p>
          </div>

          <div className="flex gap-2">
            <Dialog open={templatesDialogOpen} onOpenChange={setTemplatesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="templates-btn">
                  <Download className="w-4 h-4" /> Templates
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif">Contract Templates</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Download blank templates to use for your contracts.
                  </p>
                  {templates.map(template => (
                    <Card key={template.id} className="border shadow-sm">
                      <CardContent className="p-4 flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-forest flex-shrink-0" />
                            <p className="font-medium text-sm truncate">{template.name}</p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.description}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadTemplate(template.id, template.filename)}
                          data-testid={`download-template-${template.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-forest hover:bg-forest-dark gap-2 disabled:opacity-50" 
                  data-testid="add-contract-btn"
                  onClick={(e) => {
                    if (!canAddEdit()) {
                      e.preventDefault();
                      requireSubscription();
                    }
                  }}
                  disabled={!canAddEdit()}
                >
                  {!canAddEdit() && <Lock className="w-4 h-4" />}
                  <Plus className="w-4 h-4" /> Add Contract
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif">New Contract</DialogTitle>
                </DialogHeader>
                <ContractForm
                  data={newContract}
                  onChange={setNewContract}
                  onSubmit={handleAddContract}
                  horses={horses}
                  riders={riders}
                  selectedFile={selectedFile}
                  onFileSelect={setSelectedFile}
                  fileInputRef={fileInputRef}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {contracts.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-16 text-center">
              <FileSignature className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <h3 className="text-lg font-semibold mb-2">No Contracts Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-4">
                Create and manage contracts for boarding, lease, sale, training, and more.
              </p>
              {templates.length > 0 && (
                <Button variant="outline" onClick={() => setTemplatesDialogOpen(true)}>
                  <Download className="w-4 h-4 mr-2" /> Download Templates
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contracts.map((contract, i) => (
              <Card
                key={contract.contract_id}
                className="border-0 shadow-card bg-white animate-slideUp overflow-hidden"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-forest/10">
                      <FileSignature className="w-5 h-5 text-forest" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{contract.title}</h3>
                      <p className="text-sm text-muted-foreground">{getContractTypeLabel(contract.contract_type)}</p>
                    </div>
                  </div>

                  {(contract.horse_id || contract.rider_id) && (
                    <div className="text-sm space-y-1 mb-4">
                      {contract.horse_id && (
                        <p><span className="text-muted-foreground">Horse:</span> {getHorseName(contract.horse_id)}</p>
                      )}
                      {contract.rider_id && (
                        <p><span className="text-muted-foreground">Rider:</span> {getRiderName(contract.rider_id)}</p>
                      )}
                    </div>
                  )}

                  {(contract.start_date || contract.end_date) && (
                    <div className="text-sm text-muted-foreground mb-4">
                      {contract.start_date && (
                        <span>{format(parseISO(contract.start_date), 'MMM d, yyyy')}</span>
                      )}
                      {contract.start_date && contract.end_date && <span> - </span>}
                      {contract.end_date && (
                        <span>{format(parseISO(contract.end_date), 'MMM d, yyyy')}</span>
                      )}
                    </div>
                  )}

                  {contract.file_url && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${contract.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-forest hover:underline mb-4"
                    >
                      <ExternalLink className="w-4 h-4" /> View Document
                    </a>
                  )}

                  <div className="flex items-center gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingContract(contract);
                        setEditDialogOpen(true);
                      }}
                      data-testid={`edit-contract-${contract.contract_id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive" data-testid={`delete-contract-${contract.contract_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Contract</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{contract.title}"? This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteContract(contract.contract_id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingContract(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Edit Contract</DialogTitle>
            </DialogHeader>
            {editingContract && (
              <ContractForm
                data={editingContract}
                onChange={setEditingContract}
                onSubmit={handleEditContract}
                isEdit={true}
                horses={horses}
                riders={riders}
                selectedFile={null}
                onFileSelect={() => {}}
                fileInputRef={fileInputRef}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
